import { useToolOwnership } from "../shared/toolOwnership"
import { useCallback, useEffect, useRef, useState } from "react"
import { useAppMessage } from "./callTransport"
import { meetings } from "services/api/meetingsClient"
import type { MeetingPoll, MeetingPollCreate, MeetingPollPanel, MeetingPollPanelResponse } from "services/api/meetingsClient"
import { useMeetingPanelEvents } from "../../useMeetingPanelEvents"
import { INSPIRE_SYNC_MESSAGE_TYPE } from "./constants"
import { createGuestSessionId } from "../shared/guestSession"

export const POLL_SYNC_MESSAGE_TYPE = "wisefriend.poll-sync"
const requestId = createGuestSessionId
export const blankPollDraft = (): MeetingPollCreate => ({ request_id: requestId(), question: "", poll_type: "single", visibility_mode: "named", results_visible: false, options: ["", ""] })
export function pollDraftError(draft: MeetingPollCreate): string {
  if (!draft.question.trim() || draft.question.trim().length > 500) return "Enter a question of 1–500 characters."
  if (draft.poll_type === "rating") return ""
  if (draft.options.length < 2 || draft.options.length > 10) return "Add 2–10 options."
  if (draft.options.some(option => !option.trim() || option.trim().length > 120)) return "Each option needs 1–120 characters."
  if (new Set(draft.options.map(option => option.trim().toLocaleLowerCase())).size !== draft.options.length) return "Options must be different."
  return ""
}
const errorMessage = (error: unknown) => {
  const data = (error as { response?: { data?: Record<string, unknown> } })?.response?.data
  if (typeof data?.detail === "string") return data.detail
  const fieldMessage = data && Object.values(data).flat().find(value => typeof value === "string")
  return typeof fieldMessage === "string" ? fieldMessage : "Unable to update polls. Please try again."
}

/** One controller owns both stage and Tools. Fetch generations prevent older responses undoing an action. */
export function useMeetingPolls({ meetingId, panel, enabled, reconnecting = false, onDisplay }: {
  meetingId: string; panel?: MeetingPollPanel; enabled: boolean; reconnecting?: boolean; onDisplay?: () => void
}) {
  const token = panel?.panel_token || ""
  const ownership = useToolOwnership(`${meetingId}:${token}:${panel?.role}`)
  const captureTool = ownership.capture
  const available = Boolean(enabled && panel?.enabled && token)
  const [data, setData] = useState<MeetingPollPanelResponse | null>(null)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState("")
  /** The API's machine-readable conflict code for the last failed action (for example `inspire_active`). */
  const [errorCode, setErrorCode] = useState("")
  const [notice, setNotice] = useState("")
  const [busy, setBusy] = useState(false)
  const [draft, setDraft] = useState<MeetingPollCreate>(blankPollDraft)
  const [creating, setCreating] = useState(false)
  const [selections, setSelections] = useState<Record<string, string[]>>({})
  const [onStage, setOnStage] = useState(true)
  const generation = useRef(0)
  const mounted = useRef(true)
  const pending = useRef(false)
  const displayKey = useRef("")
  const displayCallback = useRef(onDisplay)
  displayCallback.current = onDisplay
  useEffect(() => {
    pending.current = false; generation.current += 1
    setData(null); setDraft(blankPollDraft()); setCreating(false); setBusy(false); setError(''); setErrorCode(''); setNotice(''); setSelections({})
  }, [ownership.key])
  useEffect(() => { mounted.current = true; return () => { mounted.current = false; generation.current += 1 } }, [])
  const refresh = useCallback(async () => {
    const owns = captureTool()
    if (!available) return
    const current = ++generation.current
    setLoading(true)
    try {
      const next = await meetings.getMeetingPollPanel(meetingId, token)
      if (!owns() || current !== generation.current) return
      setData(next)
      setError("")
      const shown = next.active_poll || next.displayed_poll
      const key = shown ? `${shown.id}:${shown.status}:${shown.displayed_at || shown.launched_at}` : ""
      if (key && key !== displayKey.current) { setOnStage(true); displayCallback.current?.() }
      displayKey.current = key
    } catch (err) {
      if (owns() && current === generation.current) setError(errorMessage(err))
    } finally { if (owns() && current === generation.current) setLoading(false) }
  }, [available, meetingId, token, captureTool])
  useEffect(() => {
    if (!available) { setData(null); return }
    void refresh()
    const timer = window.setInterval(() => void refresh(), 30000)
    const online = () => void refresh()
    window.addEventListener("online", online)
    return () => { generation.current += 1; window.clearInterval(timer); window.removeEventListener("online", online) }
  }, [available, refresh])
  useEffect(() => { if (!reconnecting) void refresh() }, [reconnecting, refresh])
  useMeetingPanelEvents({ panelToken: token, realtimeUrl: data?.realtime_url, enabled: available, onReconnect: refresh, onEvent: event => {
    if (event.type.startsWith("poll.") || event.type.startsWith("inspire.") || event.type === "meeting.ended") void refresh()
  } })
  const send = useAppMessage({ onAppMessage: useCallback((event: { data?: { type?: string; meetingId?: string } }) => {
    if (event.data?.meetingId === meetingId && (event.data.type === POLL_SYNC_MESSAGE_TYPE || event.data.type === INSPIRE_SYNC_MESSAGE_TYPE)) void refresh()
  }, [meetingId, refresh]) })
  const latestRefresh = useRef(refresh); latestRefresh.current = refresh
  const run = useCallback(async (operation: () => Promise<unknown>, message: string, success?: () => void) => {
    const owns = captureTool()
    if (pending.current || !available) return false
    pending.current = true
    generation.current += 1
    setBusy(true); setError(""); setErrorCode(""); setNotice("")
    try {
      await operation()
      if (!owns()) return false
      generation.current += 1
      success?.()
      setNotice(message)
      try { send({ type: POLL_SYNC_MESSAGE_TYPE, meetingId }, "*") } catch { /* Server events and polling cover transport loss. */ }
      await latestRefresh.current()
      return owns()
    } catch (err) {
      if (owns()) {
        await latestRefresh.current()
        if (!owns()) return false
        setError(errorMessage(err))
        const code = (err as { response?: { data?: { code?: unknown } } })?.response?.data?.code
        setErrorCode(typeof code === "string" ? code : "")
      }
      return false
    } finally { if (owns()) { pending.current = false; setBusy(false) } }
  }, [available, meetingId, send, captureTool])
  const updateDraft = (patch: Partial<MeetingPollCreate>) => {
    setError("")
    setErrorCode("")
    setDraft(current => ({ ...current, ...patch, request_id: requestId() }))
  }
  /** `endInspireCard`: the presenter confirmed putting a live Inspire Card away for this poll. */
  const launch = ({ endInspireCard = false }: { endInspireCard?: boolean } = {}) => {
    const validation = pollDraftError(draft)
    if (validation) { setError(validation); return Promise.resolve(false) }
    return run(() => meetings.createMeetingPoll(meetingId, token, {
      ...draft,
      question: draft.question.trim(),
      options: draft.poll_type === "rating" ? [] : draft.options.map(option => option.trim()),
      ...(endInspireCard ? { end_inspire_card: true } : {}),
    }), "Poll launched.", () => { setCreating(false); setDraft(blankPollDraft()) })
  }
  const vote = (poll: MeetingPoll, optionIds = selections[poll.id] ?? poll.own_vote?.option_ids ?? []) => run(
    () => meetings.saveMeetingPollVote(meetingId, poll.id, token, optionIds),
    "Your vote was saved.",
    () => {
      // The vote endpoint has acknowledged this choice. Keep that acknowledgement
      // even if the subsequent totals refresh cannot complete.
      const update = (item: MeetingPoll | null | undefined) => item?.id === poll.id ? { ...item, own_vote: { option_ids: optionIds } } : item
      setData(current => current ? {
        ...current,
        active_poll: update(current.active_poll) || null,
        displayed_poll: update(current.displayed_poll) || null,
        selected_poll: update(current.selected_poll),
        polls: current.polls?.map(item => update(item)!),
        own_vote: current.selected_poll?.id === poll.id ? { option_ids: optionIds } : current.own_vote,
      } : current)
      setSelections(current => { const next = { ...current }; delete next[poll.id]; return next })
    },
  )
  const control = (poll: MeetingPoll, action: "close" | "reveal" | "dismiss") => run(() => meetings.controlMeetingPoll(meetingId, poll.id, token, action), action === "close" ? "Voting closed." : action === "reveal" ? "Results revealed." : "Results dismissed.")
  const duplicate = (poll: MeetingPoll) => { setDraft({ ...blankPollDraft(), question: poll.question, poll_type: poll.poll_type, visibility_mode: poll.visibility_mode, results_visible: poll.results_visible, options: poll.poll_type === "rating" ? ["", ""] : poll.options.map(option => option.label), rating_min_label: poll.rating_min_label, rating_max_label: poll.rating_max_label }); setCreating(true); setError("") }
  return { data, loading, error, errorCode, notice, busy, draft, creating, setCreating, updateDraft, launch, vote, control, duplicate, refresh, selections, setSelections, onStage, setOnStage, meetingId, token }
}
export type MeetingPollController = ReturnType<typeof useMeetingPolls>
